import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";

const VIDEO_ID = "tDldFoIICNQ";
const VIDEO_URL = `https://www.youtube.com/watch?v=${VIDEO_ID}`;
const THUMB = `https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`;

export default function FloatingVideo() {
  const [closed, setClosed] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Auto-minimize when user scrolls past hero to keep landing clean
  useEffect(() => {
    const onScroll = () => {
      const past = window.scrollY > window.innerHeight * 0.85;
      setMinimized(past);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-collapse to small label after 8s on desktop (so it doesn't keep distracting)
  useEffect(() => {
    const t = setTimeout(() => {
      if (window.innerWidth < 768) setCollapsed(true);
    }, 9000);
    return () => clearTimeout(t);
  }, []);

  if (closed) return null;

  const showFull = !minimized && !collapsed;

  return (
    <div
      className="fixed bottom-6 left-4 md:bottom-8 md:left-8 z-40"
      data-testid="floating-video"
    >
      <AnimatePresence mode="wait" initial={false}>
        {showFull ? (
          <motion.div
            key="full"
            initial={{ opacity: 0, y: 30, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative group"
          >
            {/* Close */}
            <button
              type="button"
              aria-label="Tutup video"
              onClick={() => setClosed(true)}
              className="absolute -top-2 -right-2 z-10 w-7 h-7 rounded-full bg-forest text-parchment shadow-md flex items-center justify-center hover:bg-gold hover:text-forest transition-colors duration-300"
              data-testid="floating-video-close"
            >
              <X size={14} />
            </button>

            <a
              href={VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="floating-video-link"
              className="block w-[200px] md:w-[260px] rounded-2xl overflow-hidden border border-gold/40 shadow-2xl bg-forest"
              style={{ boxShadow: "0 24px 48px -16px rgba(10,46,34,0.4)" }}
            >
              {/* Thumb */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={THUMB}
                  alt="Profil Pondok Tahfizh Generasi Rabbani Qurani"
                  className="absolute inset-0 w-full h-full object-cover img-warm transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/20 to-transparent" />

                {/* Play button */}
                <motion.span
                  className="absolute inset-0 flex items-center justify-center"
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <span className="relative inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/95 text-forest shadow-xl">
                    <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
                    <Play size={22} className="ml-1 fill-current" />
                  </span>
                </motion.span>

                {/* LIVE-style tag */}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600 text-white text-[10px] tracking-widest uppercase font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  YouTube
                </span>
              </div>

              {/* Caption */}
              <div className="px-3 py-3 md:px-4 md:py-3 bg-forest text-parchment">
                <p className="text-[10px] tracking-[0.3em] uppercase text-gold">Profil Pondok</p>
                <p className="mt-1 text-xs md:text-sm leading-snug line-clamp-2">
                  Saksikan kegiatan & suasana santri Generasi Rabbani Qurani
                </p>
              </div>
            </a>
          </motion.div>
        ) : (
          <motion.button
            key="mini"
            type="button"
            onClick={() => { setCollapsed(false); setMinimized(false); }}
            aria-label="Buka pratinjau video profil"
            data-testid="floating-video-mini"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative group"
          >
            <span className="absolute inset-0 rounded-full bg-red-600/50 animate-ping" />
            <span className="relative inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-red-600 text-white shadow-xl ring-4 ring-red-600/20 hover:bg-red-700 transition-colors duration-300">
              <Play size={20} className="ml-0.5 fill-current" />
            </span>
            <span className="hidden md:inline-flex absolute left-full ml-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-full bg-forest text-parchment text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              Profil Pondok
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
